/**
 * Settings Storage - verwaltet die Persistenz von Widget-Konfigurationen
 * synchronisiert zwischen localStorage und Backend
 * 
 */

import logger from '../logging/logger';
import { fetchApi, putApi, saveApi, deleteApi } from '../utils/DatabaseHelper';

// API-Endpunkt für Widget-Einstellungen
const API_ENDPOINT = 'widget-settings';

class SettingsStorage {
  constructor() {
    this.cache = new Map();
    this.isInitialized = false;
  }

  /**
   * Initialisiert den Storage durch Laden aus localStorage
   */
  initialize() {
    if (this.isInitialized) return;
    
    try {
      const storedSettings = localStorage.getItem('widgetSettings');
      if (storedSettings) {
        const parsedSettings = JSON.parse(storedSettings);
        parsedSettings.forEach(config => {
          this.cache.set(config.id, config);
        });
      }
      this.isInitialized = true;
    } catch (error) {
      logger.error('Failed to initialize settings storage from localStorage:', error);
    }
  }

  /**
   * Synchronisiert die Einstellungen mit dem Backend
   * @returns Promise<void>
   */
  async sync() {
    this.initialize();
    
    // Hole die echte User-ID aus dem globalen Auth-Objekt
    const userId = global.auth?.user?.id || 1; // Fallback auf 1 falls nicht verfügbar
    
    try {
      // Hole aktuelle Einstellungen vom Backend für den spezifischen User
      const backendSettings = await fetchApi(API_ENDPOINT, userId);
      
      // Aktualisiere Cache mit Backend-Daten
      this.cache.clear();
      
      // Wenn keine Einstellungen vorhanden sind, erstelle Default-Einstellungen
      if (!backendSettings || backendSettings.length === 0) {
        logger.log('No settings found for user, creating default settings');
        this.createDefaultConfig();
        return;
      }
      
      // Verarbeite die Backend-Einstellungen
      // Das Backend gibt ein Array mit einzelnen Widget-Datensätzen zurück
      if (Array.isArray(backendSettings)) {
        backendSettings.forEach(userData => {
          // Prüfe ob widget_config existiert und ein String ist
          if (typeof userData.widget_config === 'string') {
            try {
              const parsedConfig = JSON.parse(userData.widget_config);
              // Verwende widget_id als Schlüssel, falls vorhanden, ansonsten config.id
              const key = parsedConfig.widget_id || parsedConfig.id;
              this.cache.set(key, parsedConfig);
            } catch (parseError) {
              logger.error('Failed to parse widget_config:', parseError);
            }
          } else if (typeof userData.widget_config === 'object') {
            // Falls widget_config bereits ein Objekt ist, verwenden wir es direkt
            const config = userData.widget_config;
            const key = config.widget_id || config.id;
            this.cache.set(key, config);
          }
        });
      }
      
      // Speichere im localStorage als Cache
      this.saveToLocalStorage();
      
      logger.log('Settings synced successfully with backend for user:', userId);
    } catch (error) {
      logger.error('Failed to sync settings with backend:', error);
      // Wenn der Fehler darauf beruht, dass keine Einstellungen existieren, erstelle Default-Einstellungen
      if (error.message && error.message.includes('404')) {
        logger.log('No settings found for user, creating default settings');
        this.createDefaultConfig();
        return;
      }
      // Im Fehlerfall auf localStorage Cache zurückgreifen
      this.loadFromLocalStorage();
    }
  }
  
  /**
   * Erstellt eine Standardkonfiguration für die registrierten Widgets
   */
  createDefaultConfig() {
    // Importiere die Widget-Registry, um auf die registrierten Widgets zuzugreifen
    import('./../widgets/registry').then(({ widgetRegistry }) => {
      // Nur die gewünschten Standard-Widgets erstellen
      const defaultWidgets = [
        {
          type: 'pie-chart-overview',
          name: 'Pie Chart Overview',
          position: 0
        },
        {
          type: 'jahres-statistik-bar-chart',
          name: 'Jahresstatistik Balkendiagramm',
          position: 1
        }
      ];
      
      defaultWidgets.forEach((widgetInfo, index) => {
        // Hole die Widget-Definition aus der Registry
        const widgetDefinition = widgetRegistry.getWidget(widgetInfo.type);
        
        if (widgetDefinition) {
          const defaultConfig = {
            id: `widget-${widgetInfo.type}-${Date.now()}`,
            type: widgetInfo.type,
            title: widgetDefinition.defaultConfig.title || widgetInfo.name,
            isActive: widgetDefinition.defaultConfig.isActive !== undefined ? widgetDefinition.defaultConfig.isActive : true,
            position: widgetInfo.position,
            size: widgetDefinition.defaultConfig.size || { width: 1, height: 1 },
            globalSettings: widgetDefinition.defaultConfig.globalSettings || {}
          };
          
          this.setConfig(defaultConfig);
        }
      });
      
      // Speichere die Standardkonfiguration im Backend
      this.saveToBackend(this.getAllConfigs());
    });
  }

  /**
   * Speichert die Einstellungen im Backend
   * @param configs Array der Widget-Konfigurationen
   * @returns Promise<boolean> true bei Erfolg
   */
  async saveToBackend(configs) {
    // Hole die echte User-ID aus dem globalen Auth-Objekt
    const userId = global.auth?.user?.id || 1; // Fallback auf 1 falls nicht verfügbar
    
    try {
      // Für jedes Widget eine separate Anfrage senden
      const savePromises = configs.map(async (config) => {
        // Direkt versuchen, das Widget zu speichern
        // Verwende saveApi zum Erstellen
        return await saveApi(API_ENDPOINT, {
          user_id: userId,
          widget_config: JSON.stringify(config),
          widget_id: config.id
        });
      });
      
      // Warte auf alle Speicheroperationen
      await Promise.all(savePromises);
      
      // Bei Erfolg auch im localStorage speichern
      this.saveToLocalStorage();
      return true;
    } catch (error) {
      logger.error('Failed to save settings to backend:', error);
      return false;
    }
  }

  /**
   * Speichert die aktuellen Einstellungen im localStorage
   */
  saveToLocalStorage() {
    try {
      const configs = Array.from(this.cache.values());
      localStorage.setItem('widgetSettings', JSON.stringify(configs));
    } catch (error) {
      logger.error('Failed to save settings to localStorage:', error);
    }
  }

  /**
   * Lädt Einstellungen aus dem localStorage
   */
  loadFromLocalStorage() {
    try {
      const storedSettings = localStorage.getItem('widgetSettings');
      if (storedSettings) {
        const parsedSettings = JSON.parse(storedSettings);
        this.cache.clear();
        parsedSettings.forEach(config => {
          this.cache.set(config.id, config);
        });
      }
    } catch (error) {
      logger.error('Failed to load settings from localStorage:', error);
    }
  }

  /**
   * Fügt eine neue Widget-Konfiguration hinzu oder aktualisiert eine vorhandene
   * @param config Die Widget-Konfiguration
   */
  setConfig(config) {
    this.initialize();
    // Verwende widget_id als Schlüssel, falls vorhanden, ansonsten config.id
    const key = config.widget_id || config.id;
    this.cache.set(key, config);
    this.saveToLocalStorage();
  }

  /**
   * Gibt eine Widget-Konfiguration anhand ihrer ID zurück
   * @param id Die ID der Widget-Konfiguration
   * @returns Die Widget-Konfiguration oder undefined
   */
  getConfig(id) {
    this.initialize();
    return this.cache.get(id);
  }

  /**
   * Entfernt eine Widget-Konfiguration
   * @param id Die ID der Widget-Konfiguration
   */
  removeConfig(id) {
    this.initialize();
    // Hole die echte User-ID aus dem globalen Auth-Objekt
    const userId = global.auth?.user?.id || 1; // Fallback auf 1 falls nicht verfügbar
    
    // Entferne direkt aus dem Backend
    deleteApi(API_ENDPOINT, `${userId}/${id}`).then(() => {
      logger.log(`Widget ${id} successfully removed from backend`);
    }).catch(error => {
      logger.error(`Error removing widget ${id} from backend:`, error);
      // Entferne auch lokal, auch wenn der Server-Fehler auftritt
      this.cache.delete(id);
      this.saveToLocalStorage();
    });
    
    // Entferne aus dem Cache
    this.cache.delete(id);
    this.saveToLocalStorage();
  }

  /**
   * Gibt alle Widget-Konfigurationen zurück
   * @returns Array aller Widget-Konfigurationen
   */
  getAllConfigs() {
    this.initialize();
    return Array.from(this.cache.values());
  }

  /**
   * Aktualisiert eine Widget-Konfiguration
   * @param id Die ID der Widget-Konfiguration
   * @param updates Teilobjekt mit den zu aktualisierenden Eigenschaften
   */
  updateConfig(id, updates) {
    this.initialize();
    const existingConfig = this.cache.get(id);
    
    if (existingConfig) {
      const updatedConfig = { ...existingConfig, ...updates };
      // Verwende widget_id als Schlüssel, falls vorhanden, ansonsten config.id
      const key = updatedConfig.widget_id || updatedConfig.id;
      this.cache.set(key, updatedConfig);
      this.saveToLocalStorage();
    }
  }
}

// Export einer singleton Instanz der SettingsStorage
export const settingsStorage = new SettingsStorage();
